import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { profileApi, type Message } from '@/services/api';
import { useMessages } from '@/hooks/useMessages';
import { useAuth } from '@/context/AuthContext';
import type { ChatConversation, ChatMessage } from '@/services/api';

type TabView = 'notifications' | 'chats' | 'chatDetail';

export default function MessageCenter() {
  const { user } = useAuth();
  const { conversations, loadConversations, loadMessages, markConversationRead, sendMessage } = useMessages();

  // Notifications
  const [notifications, setNotifications] = useState<Message[]>([]);
  const [notifFilter, setNotifFilter] = useState<'all' | 'inquiry' | 'system'>('all');
  const [loadingNotif, setLoadingNotif] = useState(true);
  const [errorNotif, setErrorNotif] = useState<string | null>(null);

  // Chats
  const [selectedConv, setSelectedConv] = useState<ChatConversation | null>(null);
  const [convMessages, setConvMessages] = useState<ChatMessage[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const [view, setView] = useState<TabView>('chats');

  // Load notifications
  useEffect(() => {
    profileApi
      .messages()
      .then((data) => {
        setNotifications(data);
        setLoadingNotif(false);
      })
      .catch((err) => {
        setErrorNotif(err instanceof Error ? err.message : '加载失败');
        setLoadingNotif(false);
      });
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const markNotifRead = (id: number) => {
    setNotifications((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    profileApi.markMessageRead(id).catch(() => {});
  };

  const openConversation = async (conv: ChatConversation) => {
    setSelectedConv(conv);
    setView('chatDetail');
    const msgs = await loadMessages(conv.peerId);
    setConvMessages(msgs);
    await markConversationRead(conv.peerId);
    loadConversations();
  };

  const handleReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || !selectedConv || !user) return;
    setSending(true);
    await sendMessage(selectedConv.peerId, {
      listingId: selectedConv.listingId || undefined,
      listingType: selectedConv.listingType || undefined,
      listingTitle: selectedConv.listingTitle || undefined,
      receiverId: selectedConv.peerId,
      receiverName: selectedConv.counterparty,
      content: trimmed,
    });
    setReplyText('');
    setSending(false);
    const msgs = await loadMessages(selectedConv.peerId);
    setConvMessages(msgs);
    loadConversations();
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return '刚刚';
    if (diffMin < 60) return `${diffMin}分钟前`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}小时前`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return `${diffDay}天前`;
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const filteredNotif = notifFilter === 'all' ? notifications : notifications.filter((m) => m.type === notifFilter);
  const unreadNotif = notifications.filter((m) => !m.read).length;
  const chatUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  // Back to chat list from detail
  if (view === 'chatDetail' && selectedConv) {
    return (
      <div>
        <button
          onClick={() => { setView('chats'); setSelectedConv(null); }}
          className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors mb-4 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回对话列表
        </button>

        <div className="bg-background-50 border border-background-200 rounded-lg flex flex-col min-h-[450px]">
          {/* Header */}
          <div className="px-5 py-4 border-b border-background-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-foreground-900 text-base font-bold font-heading">{selectedConv.counterparty}</p>
                {selectedConv.listingType && selectedConv.listingId > 0 && (
                  <Link
                    to={`/${selectedConv.listingType}/${selectedConv.listingId}`}
                    className="text-primary-600 hover:text-primary-700 text-xs mt-0.5 inline-block cursor-pointer"
                  >
                    查看挂牌详情 <i className="ri-arrow-right-up-line text-xs"></i>
                  </Link>
                )}
              </div>
              {selectedConv.listingType && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  selectedConv.listingType === 'supply' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                }`}>
                  {selectedConv.listingType === 'supply' ? '供给方' : '需求方'}
                </span>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 px-5 py-4 space-y-4 overflow-y-auto max-h-[300px]">
            {convMessages.length === 0 ? (
              <p className="text-foreground-400 text-sm text-center py-8">暂无消息记录</p>
            ) : (
              convMessages.map((msg) => {
                const isMine = user ? msg.senderId === user.id : false;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-lg px-4 py-3 ${
                      isMine
                        ? 'bg-primary-500 text-background-50 rounded-br-sm'
                        : 'bg-background-100 text-foreground-800 rounded-bl-sm'
                    }`}>
                      {!isMine && <p className="text-foreground-400 text-xs mb-1">{msg.senderName}</p>}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1.5 ${isMine ? 'text-background-50/70' : 'text-foreground-400'}`}>
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Reply */}
          <div className="px-5 py-4 border-t border-background-200">
            <div className="flex items-end gap-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                maxLength={500}
                placeholder={`回复 ${selectedConv.counterparty}…`}
                className="flex-1 px-4 py-2.5 text-sm text-foreground-900 bg-background-100 rounded-lg border border-background-200 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 placeholder:text-foreground-300 h-10 max-h-24"
              />
              <button
                onClick={handleReply}
                disabled={!replyText.trim() || replyText.length > 500 || sending}
                className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                {sending ? '发送中' : '发送'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Top tabs */}
      <div className="flex items-center gap-2 mb-6 bg-background-100 rounded-full px-1 py-1 w-fit">
        <button
          onClick={() => setView('notifications')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
            view === 'notifications' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          系统通知
          {unreadNotif > 0 && (
            <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
              {unreadNotif}
            </span>
          )}
        </button>
        <button
          onClick={() => setView('chats')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
            view === 'chats' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          站内对话
          {chatUnread > 0 && (
            <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
              {chatUnread}
            </span>
          )}
        </button>
      </div>

      {/* Notifications view */}
      {view === 'notifications' && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setNotifFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                notifFilter === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
              }`}
            >
              全部
            </button>
            <button
              onClick={() => setNotifFilter('inquiry')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                notifFilter === 'inquiry' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
              }`}
            >
              询价消息
            </button>
            <button
              onClick={() => setNotifFilter('system')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                notifFilter === 'system' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
              }`}
            >
              系统通知
            </button>
          </div>

          {loadingNotif ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
            </div>
          ) : errorNotif ? (
            <div className="text-center py-12">
              <p className="text-foreground-500 text-sm">{errorNotif}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer font-medium"
              >
                重新加载
              </button>
            </div>
          ) : filteredNotif.length === 0 ? (
            <p className="text-foreground-400 text-sm py-12 text-center">暂无消息</p>
          ) : (
            <div className="space-y-3">
              {filteredNotif.map((msg) => (
                <div
                  key={msg.id}
                  className={`bg-background-50 border rounded-lg p-5 cursor-pointer transition-colors hover:border-primary-300 ${
                    !msg.read ? 'border-primary-200 bg-primary-50/30' : 'border-background-200'
                  }`}
                  onClick={() => markNotifRead(msg.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {!msg.read && <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></span>}
                        <span className="text-foreground-900 text-sm font-medium">{msg.title}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded flex-shrink-0 ${
                          msg.type === 'inquiry' ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'
                        }`}>
                          {msg.type === 'inquiry' ? '询价' : '系统'}
                        </span>
                      </div>
                      <p className="text-foreground-600 text-sm mt-1.5 leading-relaxed">{msg.content}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                        <span>{msg.from}</span>
                        <span>{msg.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Chats view */}
      {view === 'chats' && (
        <>
          {conversations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-chat-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm mt-3">暂无站内对话</p>
              <p className="text-foreground-400 text-xs mt-1">浏览供给或需求，向对方发送站内信开始对话</p>
              <Link
                to="/supply"
                className="inline-block mt-4 px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
              >
                浏览供给列表
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <button
                  key={conv.peerId}
                  onClick={() => openConversation(conv)}
                  className="w-full text-left bg-background-50 border border-background-200 rounded-lg px-5 py-4 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-900 text-sm font-medium">{conv.counterparty}</span>
                        {conv.listingType && (
                          <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                            conv.listingType === 'supply' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                          }`}>
                            {conv.listingType === 'supply' ? '供给' : '需求'}
                          </span>
                        )}
                      </div>
                      <p className="text-foreground-500 text-sm mt-1 truncate">{conv.lastMessage}</p>
                      {conv.listingTitle && <p className="text-foreground-300 text-xs mt-1 truncate">{conv.listingTitle}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className="text-foreground-400 text-xs">{formatTime(conv.lastMessageTime)}</span>
                      {conv.unreadCount > 0 && (
                        <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
                          {conv.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}